import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { Box } from "@chakra-ui/react";
import { keymap } from "prosemirror-keymap";
import { undo, redo, history } from "prosemirror-history";
import { baseKeymap } from "prosemirror-commands";
import useDebounce from "../../../_hooks/useDebounce";
import ProseMirror from "../ProseMirror/ProseMirror";
import useProseMirror from "../ProseMirror/useProseMirror";
import {
  mentionPlugin,
  mentionPluginKey,
} from "../../../_plugins/prosemirror/mention-plugin";
import { hashtagHighlighterPlugin } from "../../../_plugins/prosemirror/hashtag-highlighter-plugin";
import { linkHighlighterPlugin } from "../../../_plugins/prosemirror/link-highlighter-plugin";
import { atMentionHighlighterPlugin } from "../../../_plugins/prosemirror/at-mention-highlighter-plugin";
import { linkMenuPlugin } from "../../../_plugins/prosemirror/link-menu-plugin";
import { selectedTextMenuPlugin } from "../../../_plugins/prosemirror/selected-text-menu-plugin";
import { escapeRegExp } from "./utils";
import * as AdvancedMessageEntitiesTypes from "./AdvancedMessageEntitiesTypes.constants";
import AdvancedMessageMention from "./AdvancedMessageMention";
import AdvancedMessageLinkMenu from "./AdvancedMessageLinkMenu";
import { schema, fromJSON, toJSON } from "./advancedMessageSchema";
import AdvancedMessageSelectedTextMenu from "./AdvancedMessageSelectedTextMenu";

const likeLinkStyles = {
  color: "brand.primary",
};

const wrapperStyles = ({
  styles: customStyles,
  highlightHashtags,
  highlightLinks,
  highlightAtMentions,
  mentionEnabled,
}) => {
  const { ".ProseMirror": proseMirrorCustomStyles, ...restCustomStyles } =
    customStyles || {};
  let styles = {
    ".ProseMirror": {
      overflowY: "auto",
      paddingTop: "8px",
      paddingBottom: "8px",
      paddingInlineStart: "3",
      paddingInlineEnd: "3",
      minHeight: "6rem",
      outline: "none",
      borderRadius: "sm",
      border: "none",
      fontSize: "sm",
      lineHeight: "short",
      color: "inherit",
      cursor: "text",
      ...proseMirrorCustomStyles,
      ":focus": {
        boxShadow: "rgb(206 146 49 / 50%) 0px 0px 0px 1px",
      },
    },
  };

  if (highlightHashtags) {
    styles = {
      ...styles,
      '[data-mark][data-type="hashtag"]': {
        ...likeLinkStyles,
      },
    };
  }

  if (highlightLinks) {
    styles = {
      ...styles,
      '[data-mark][data-type="link"]': {
        ...likeLinkStyles,
      },
    };
  }

  if (highlightAtMentions) {
    styles = {
      ...styles,
      '[data-mark][data-type="at-mention"]': {
        ...likeLinkStyles,
      },
    };
  }

  if (mentionEnabled) {
    styles = {
      ...styles,
      '[data-entity][data-type="mention"]': {
        paddingLeft: "0.5",
        paddingRight: "0.5",
        color: "brand.primary",
        backgroundColor: "whiteAlpha.400",
      },
      '[data-mark][data-type="mention-typing"]': {
        paddingLeft: "0.5",
        paddingRight: "0.5",
        color: "brand.primary",
        backgroundColor: "whiteAlpha.400",
        border: "1px solid",
        borderColor: "whiteAlpha.400",
      },
    };
  } else {
    styles = {
      ...styles,
      '[data-entity][data-type="mention"]': {
        paddingLeft: "0.5",
        paddingRight: "0.5",
        backgroundColor: "whiteAlpha.400",
      },
    };
  }

  styles = {
    ...styles,
    '[data-mark][data-type="link-menu"]': {
      color: "brand.primary",
      backgroundColor: "orange.100",
    },
  };

  return {
    ...styles,
    ...restCustomStyles,
  };
};

const mentionItemToSchema = (item) => ({
  type: AdvancedMessageEntitiesTypes.MENTION,
  text: item.text,
  source: item.source,
  id: item.id,
  name: item.name,
});

export const replaceLinks = (view, items) => {
  let transaction = view.state.tr;
  let positionOffset = 0;

  items.forEach((item) => {
    if (item.from === item.to) {
      return;
    }

    transaction.doc.descendants((node, position) => {
      if (!node.text) {
        return;
      }

      const linkRegExp = new RegExp(
        `(^|\\s)(${escapeRegExp(item.from)})($|\\s)`,
        "ig"
      );

      Array.from(node.text.matchAll(linkRegExp)).forEach((match) => {
        const value = match[2];
        const index = (match.index || 0) + match[1].length;
        const from = position + index + positionOffset;
        const to = from + value.length;
        const changeOffset = item.to.length - value.length;

        transaction = transaction.insertText(item.to, from, to);
        positionOffset += changeOffset;
      });
    });
  });

  if (transaction.docChanged) {
    view.dispatch(transaction);
  }
};

// export const replaceAtRange = (view, range, value) => {
//   const transaction = view.state.tr.insertText(value, range.from, range.to);

//   if (transaction.docChanged) {
//     view.dispatch(transaction);
//   }
// };

const AdvancedMessageField = forwardRef(
  (
    {
      state,
      placeholder,
      onChange,
      onFocus,
      onBlur,
      onPaste,
      highlightHashtags,
      highlightLinks,
      highlightAtMentions,
      mentionEnabled,
      mentionGetItems,
      mentionAllowSpaces,
      onMentionSelect,
      linkMenuEnabled,
      onToggleLinkDisabling,
      getLinkData,
      selectedTextMenuEnabled,
      getSelectedTextMenu,
      ...rest
    },
    ref
  ) => {
    const viewRef = useRef(null);
    const [focused, setFocused] = useState(false);
    const [mentionLoading, setMentionLoadings] = useState(false);
    const [mentionItems, setMentionItems] = useState([]);
    const [mentionNode, setMentionNode] = useState(null);
    const [linkMenuData, setLinkMenuData] = useState(null);
    const [selectedTextMenuData, setSelectedTextMenuData] = useState(null);
    const [contentState, setContentState, reconfigureState] = useProseMirror({
      schema,
      doc: schema.nodeFromJSON(toJSON(state)),
      plugins: [
        mentionEnabled
          ? mentionPlugin({
              allowSpaces: mentionAllowSpaces,
              debounceTime: 500,
              onChange: ({ node }) => {
                setMentionNode(node || null);
              },
              onDebouncedChange: ({ text }) => {
                setMentionLoadings(true);

                mentionGetItems(text, (items) => {
                  setMentionItems(items);
                  setMentionLoadings(false);
                });
              },
              onExit: () => {
                setMentionItems([]);
                setMentionNode(null);
              },
            })
          : null,
        highlightHashtags ? hashtagHighlighterPlugin() : null,
        highlightLinks ? linkHighlighterPlugin() : null,
        highlightAtMentions ? atMentionHighlighterPlugin() : null,
        linkMenuEnabled
          ? linkMenuPlugin({
              onEnter: (data) => {
                setLinkMenuData(data);
              },
              onExit: () => {
                setLinkMenuData(null);
              },
            })
          : null,
        selectedTextMenuEnabled
          ? selectedTextMenuPlugin({
              onChange: ({ view, range }) => {
                const getVirtualBoundingClientRect = () => {
                  const start = view.coordsAtPos(range.from);
                  const end = view.coordsAtPos(range.to);
                  const botom = Math.max(start.bottom, end.bottom);
                  const left = Math.min((start.right + end.right) / 2, end.right);

                  return {
                    width: 0,
                    height: 0,
                    top: botom,
                    right: left,
                    bottom: botom,
                    left: left,
                  };
                };

                setSelectedTextMenuData({
                  node: {
                    getBoundingClientRect: getVirtualBoundingClientRect,
                  },
                  text: view.state.doc.textBetween(range.from, range.to, "\0", "\0"),
                  range,
                  view,
                });
              },
              onExit: () => {
                setSelectedTextMenuData(null);
              },
            })
          : null,
        history(),
        keymap({
          "Mod-z": undo,
          "Mod-y": redo,
        }),
        keymap(baseKeymap),
      ].filter((p) => p !== null),
    });
    const debouncedContentState = useDebounce(contentState, 50);

    const selectedTextMenu = useMemo(() => {
      if (!selectedTextMenuData || !getSelectedTextMenu) {
        return null;
      }

      return getSelectedTextMenu({
        text: selectedTextMenuData.text,
      });
    }, [getSelectedTextMenu, selectedTextMenuData]);

    const handleMentionSelect = useCallback(
      (item) => {
        const view = viewRef.current?.view;
        const { range } = mentionPluginKey.getState(view.state);

        if (item) {
          const node = view.state.schema.nodes.mention.create(
            mentionItemToSchema(item)
          );

          view.dispatch(view.state.tr.replaceWith(range.from, range.to, node));
          view.focus();

          onMentionSelect?.(item);
        }
      },
      [viewRef, onMentionSelect]
    );

    const handleMentionExit = useCallback(() => {
      setMentionNode(null);
    }, [setMentionNode]);

    const handleLinkMenuExit = useCallback(() => {
      setLinkMenuData(null);
    }, [setLinkMenuData]);

    const handleToggleLinkDisabling = useCallback(
      (range, text) => {
        onToggleLinkDisabling?.(range, text);
        setLinkMenuData(null);
      },
      [setLinkMenuData, onToggleLinkDisabling]
    );

    const handleViewFocus = useCallback(() => {
      viewRef.current?.view.focus();
    }, [viewRef]);

    const handleChange = useCallback(
      (newContentState) => {
        setContentState(newContentState);
      },
      [setContentState]
    );

    const handleFocus = useCallback(() => {
      setFocused(true);
      onFocus?.();
    }, [onFocus]);

    const handleBlur = useCallback(() => {
      setFocused(false);
      onBlur?.();

      // @NOTE add timeout to handle click event before close menu
      setTimeout(() => {
        setSelectedTextMenuData(null);
      }, 250);
    }, [onBlur]);

    useEffect(() => {
      if (focused) {
        onChange(fromJSON(contentState.doc.toJSON()));
      }
    }, [debouncedContentState]);

    useEffect(() => {
      if (!focused) {
        onChange(fromJSON(contentState.doc.toJSON()));
      }
    }, [contentState]);

    useImperativeHandle(ref, () => ({
      get view() {
        return viewRef.current?.view;
      },
      update() {
        reconfigureState({
          doc: contentState.schema.nodeFromJSON(toJSON(state)),
        });
      },
    }));

    return (
      <Box
        {...rest}
        sx={wrapperStyles({
          styles: rest?.sx,
          highlightHashtags,
          highlightLinks,
          highlightAtMentions,
          mentionEnabled,
        })}
      >
        <ProseMirror
          ref={viewRef}
          state={contentState}
          placeholder={placeholder}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          handlePaste={onPaste}
          attributes={{
            tabindex: "0",
          }}
        />
        {mentionEnabled ? (
          <AdvancedMessageMention
            loading={mentionLoading}
            items={mentionItems}
            node={mentionNode}
            onSelect={handleMentionSelect}
            onExit={handleMentionExit}
            onViewFocus={handleViewFocus}
          />
        ) : null}
        {linkMenuEnabled ? (
          <AdvancedMessageLinkMenu
            node={linkMenuData?.node}
            text={linkMenuData?.text}
            range={linkMenuData?.range}
            onExit={handleLinkMenuExit}
            onToggleLinkDisabling={
              onToggleLinkDisabling ? handleToggleLinkDisabling : null
            }
            getLinkData={getLinkData}
          />
        ) : null}
        {selectedTextMenuEnabled ? (
          <AdvancedMessageSelectedTextMenu
            node={selectedTextMenuData?.node}
            text={selectedTextMenuData?.text}
            range={selectedTextMenuData?.range}
            menu={selectedTextMenu}
          />
        ) : null}
      </Box>
    );
  }
);

AdvancedMessageField.propTypes = {
  state: PropTypes.shape({
    message: PropTypes.string,
    entitiesMap: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string.isRequired,
        offset: PropTypes.number.isRequired,
        length: PropTypes.number.isRequired,
        [PropTypes.string]: PropTypes.string,
      })
    ),
  }),
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onPaste: PropTypes.func,
  placeholder: PropTypes.string,
  highlightHashtags: PropTypes.bool,
  highlightLinks: PropTypes.bool,
  highlightAtMentions: PropTypes.bool,
  mentionEnabled: PropTypes.bool,
  mentionGetItems: PropTypes.func,
  mentionAllowSpaces: PropTypes.bool,
  onMentionSelect: PropTypes.func,
  linkMenuEnabled: PropTypes.bool,
  onToggleLinkDisabling: PropTypes.func,
  getLinkData: PropTypes.func,
  selectedTextMenuEnabled: PropTypes.bool,
  getSelectedTextMenu: PropTypes.func,
};

AdvancedMessageField.defaultProps = {
  state: undefined,
  onFocus: undefined,
  onBlur: undefined,
  onPaste: undefined,
  placeholder: undefined,
  highlightHashtags: undefined,
  highlightLinks: undefined,
  highlightAtMentions: undefined,
  mentionEnabled: undefined,
  mentionGetItems: undefined,
  mentionAllowSpaces: undefined,
  onMentionSelect: undefined,
  linkMenuEnabled: undefined,
  onToggleLinkDisabling: undefined,
  getLinkData: undefined,
  selectedTextMenuEnabled: undefined,
  getSelectedTextMenu: undefined,
};

export default AdvancedMessageField;
